import axios from "axios";
import { getVisitorId } from "./visitorId";
import { currentUtmParams } from "./visitUtm";
import { INTEGRATION_SERVER_URL, CHECKOUT_PREFIX } from "../config/config";

/**
 * Thin client over the existing regional-plans endpoints for WEBINAR
 * checkouts. Each webinar is a (region = "webinar", version = slug) pair.
 * Everything the page renders comes from the server config — adding a new
 * webinar needs only a backend config entry, no frontend change.
 *
 * - GET  /apps/regionalplans/t1/config/webinar/:slug
 * - POST /apps/regionalplans/t1/create-intent-for-plans
 */

const REGION = "webinar";

const client = axios.create({
  baseURL: INTEGRATION_SERVER_URL + "/regionalplans/t1",
  timeout: 15000,
  headers: { "Content-Type": "application/json" },
});

// The visitor id rides on requests the page already makes, so page-visit
// analytics costs no extra round trip. See services/visitorId.
client.interceptors.request.use((cfg) => {
  try {
    cfg.headers = { ...(cfg.headers || {}), "x-visitor-id": getVisitorId() };
    // The ad's utm params are in the page url, not on this request — forward
    // them so a recorded visit can be attributed to the campaign.
    if (String(cfg.url || "").startsWith("/config/")) {
      cfg.params = { ...(cfg.params || {}), ...currentUtmParams() };
    }
  } catch {
    /* analytics must never block a request */
  }
  return cfg;
});

const normalizeError = (error) => {
  if (error.response) {
    return Promise.reject({
      message: error.response.data?.message || "Server error",
      errors: error.response.data?.errors,
      status: error.response.status,
      data: error.response.data,
    });
  }
  if (error.request) {
    return Promise.reject({
      message: "Network error — unable to connect to server",
      status: 0,
    });
  }
  return Promise.reject({
    message: error.message || "Unknown error occurred",
    status: -1,
  });
};

client.interceptors.response.use((r) => r.data, normalizeError);

/**
 * Maps the raw regional-plans config response into the flat shape the
 * generic webinar page renders. The response is the full region config
 * spread + { plans, planOptions, addons }.
 */
const mapToWebinar = (slug, raw) => {
  const plan = (raw.plans || [])[0] || {};
  const billingPeriod = plan.fixedBillingPeriod || "oneTime";
  const periodPricing = raw.planOptions?.[billingPeriod] || {};
  const pricing = periodPricing[plan.id] || {};

  const price = Number(pricing.price || 0);
  const mrp = Number(pricing.originalPrice || 0);

  const includes = Array.isArray(plan.features)
    ? plan.features.map((f) => (typeof f === "string" ? f : f.text))
    : [];

  return {
    slug,
    productName: plan.title || raw.title,
    tagline: plan.subtitle || raw.subtitle || "",
    price,
    mrp: mrp || price,
    currency: raw.currencySymbol,
    currencyCode: raw.currency,
    billing: pricing.billed || "",
    discountLabel: raw.popularBadgeText || plan.highlight || "",
    savings: mrp > price ? mrp - price : 0,
    ctaText: raw.checkoutButtonText || "",
    couponEnabled: Boolean(raw.couponEnabled),
    includes,
    // Backend-driven, config-owned content:
    formFields: Array.isArray(raw.formFields) ? raw.formFields : [],
    whyJoin: raw.whyJoin || null,
    dateTime: raw.webinarDetails?.dateTime || "",
    eventTimestamp: raw.webinarDetails?.eventTimestamp || "",
    eyebrow: raw.eyebrow || "",
    valueChips: Array.isArray(raw.valueChips) ? raw.valueChips : [],
    host: raw.host || null,
    urgencyText: raw.urgencyText || "",
    trustText: raw.trustText || "",
    reassurance: raw.reassurance || "",
    // Server-verified plan reference used by create-intent-for-plans.
    _plan: {
      id: plan.id,
      title: plan.title,
      price,
      billingPeriod,
    },
  };
};

class WebinarApiService {
  async getWebinarConfig(slug) {
    const envelope = await client.get(`/config/${REGION}/${slug}`);
    return mapToWebinar(slug, envelope.data);
  }

  /**
   * @param slug
   * @param webinar  the flat shape from getWebinarConfig (carries formFields + _plan)
   * @param responses  answers keyed by field.key
   * @param countryContext { dialCode, isoCode }
   * @param utmParams
   */

  async verifyCoupon(couponCode, amount, slug) {
    try {
      const res = await axios.post(
        `${INTEGRATION_SERVER_URL}/coupons/t1/verify`,
        { couponCode, amount, applyTo: `${REGION}-${slug}` },
      );
      return res.data;
    } catch (err) {
      if (err && err.response && err.response.data) {
        return err.response.data;
      }
      return { success: false, message: "Failed to verify coupon" };
    }
  }

  async createPaymentIntent(
    slug,
    webinar,
    responses,
    countryContext = { dialCode: "+91", isoCode: "IN" },
    utmParams = {},
    couponCode = null,
  ) {
    // Split answers into standard columns (via each field's `column` hint)
    // and generic custom responses. Keeps the backend persistence generic.
    const standard = {};
    const customResponses = {};
    (webinar.formFields || []).forEach((field) => {
      const value = responses[field.key];
      if (value == null || value === "") return;
      if (field.column) standard[field.column] = value;
      else customResponses[field.key] = value;
    });

    const payload = {
      // Ties this conversion back to the page view that produced it.
      visitorId: getVisitorId(),
      fullName: standard.fullName,
      ...(standard.email ? { email: standard.email } : {}),
      phone: standard.phone,
      ...(standard.organizationName
        ? { organizationName: standard.organizationName }
        : {}),
      ...(standard.organizationSize
        ? { organizationSize: standard.organizationSize }
        : {}),
      customResponses,
      region: REGION,
      version: slug,
      currency: webinar.currencyCode,
      plan: webinar._plan,
      addons: [],
      totalAmount: webinar.price,
      currencySymbol: webinar.currency,
      source: "aisensy_webinar",
      countryCallingCode: countryContext.dialCode,
      isoCode: countryContext.isoCode,
      cancelUrl: `${CHECKOUT_PREFIX}/webinar/${slug}`,
      redirectUrl: `${CHECKOUT_PREFIX}/thankyou-webinar`,
      utmParams,
      ...(couponCode ? { couponCode } : {}),
    };

    const envelope = await client.post("/create-intent-for-plans", payload);
    return envelope.data; // { paymentGateway, intentId, keyId, currency, customerId, ... }
  }
}

export const webinarApiService = new WebinarApiService();
export default webinarApiService;
